<template>
  <a-modal v-model:open="open" :title="modalTitle" @ok="handleOk" :width="modalWidth">
    <div class="h-80 flex justify-between">
      <div class="left mr-4 flex flex-1 flex-col">
        <div class="flex">
          <a-input-search
            v-model:value="searchText"
            placeholder="请输入关键词查询"
            @search="handleSearch"
            @change="handleChange"
            class="mr-2 flex-1"
            v-if="!isAffiliatedOrganization"
          />
          <slot name="filterSearch" />
        </div>
        <div style="border: 1px solid #ccc" class="mt-2 h-0 flex-1 overflow-auto">
          <a-tree
            v-if="treeDataShow.length"
            v-model:selectedKeys="selectedKeysInModal"
            v-model:expandedKeys="expandedKeys"
            v-model:loadedKeys="loadedKeys"
            v-model:checkedKeys="checkedKeysInModal"
            :load-data="props.loadData"
            :tree-data="treeDataShow"
            :multiple
            show-icon
            :checkable="checkable"
            :default-expand-all="defaultExpandAll"
          >
            <!-- 通过便利实现插槽透传 -->
            <template v-for="(_, key) in treeSlot" :key="key" #[key]="slotProps">
              <slot :name="key" v-bind="slotProps"></slot>
            </template>
          </a-tree>
          <a-empty v-else class="mt-6" />
        </div>
      </div>
      <div class="right flex flex-1 flex-col">
        <div class="flex justify-between">
          <span>已选</span>
          <span
            class="cursor-pointer c-red"
            @click="
              () => {
                selectedKeysInModal = []
                checkedKeysInModal = []
              }
            "
            >清空列表</span
          >
        </div>
        <div style="border: 1px solid #ccc" class="mt-4 h-0 flex-1 overflow-auto">
          <slot name="checkedList" :checkedItems="checkedItems" v-if="checkable">
            <div v-for="item in getSelectedList(checkedItems)" :key="item.key" class="border-bottom-px flex justify-between p-2">
              <span>{{ item.title }}</span>
              <DeleteOutlined class="cursor-pointer text-xl" @click="deleteOneSelect(item.key as string)" />
            </div>
          </slot>
          <slot name="selectedList" :selectedItems="selectedItems" v-else>
            <div v-for="item in selectedItems" :key="item.key" class="border-bottom-px flex justify-between p-2">
              <span>{{ item.title }}</span>
              <DeleteOutlined class="cursor-pointer text-xl" @click="deleteOneSelect(item.key as string)" />
            </div>
          </slot>
        </div>
      </div>
    </div>
  </a-modal>
</template>
<script lang="ts" setup>
import { DeleteOutlined } from '@ant-design/icons-vue'
import { omit, debounce, isArray } from 'lodash-es'
import { useTreeDataMemory } from '@/hooks'
import { type Slot } from 'vue'
import { treeGetItemFromSourceList } from '@/utils'
import type { ITreeSelectModalProps } from './types'
import type { IValueType, IValueTypeMultiple, IValueTypeSingle } from '@/components/types'
import type { DataNode } from 'ant-design-vue/es/tree'
import { searchTreeData } from '@/utils'
import type { TreeProps } from 'ant-design-vue'
import { EnumNumberValue } from '@/enum/number'
const open = defineModel<boolean>({ required: true })
const checkable = defineModel<TreeProps['checkable']>('checkable')
const expandedKeys = defineModel<TreeProps['expandedKeys']>('expandedKeys')
const loadedKeys = defineModel<TreeProps['loadedKeys']>('loadedKeys')
const selectedKeysInModal = ref<IValueTypeMultiple>([])
const checkedKeysInModal = ref<IValueTypeMultiple>([])
const defaultExpandAll = defineModel<TreeProps['defaultExpandAll']>('defaultExpandAll', {
  default: true,
})
const props = withDefaults(defineProps<ITreeSelectModalProps>(), {
  modalWidth: 800,
  doNotCloseOnKeysNull: false,
  isAffiliatedOrganization: false, //是否是所属组织
})
const treeDataShow = ref<DataNode[]>([])

const changeTreeDataShow = (searchText: string) => {
  treeDataShow.value = searchTreeData(searchText, props.treeData)
}
const searchText = ref<string>('')
watch(
  () => props.treeData,
  () => {
    changeTreeDataShow(searchText.value)
    //所属组织默认展开第一级
    if (props?.isAffiliatedOrganization) {
      expandedKeys.value = [...(expandedKeys.value || []), props?.treeData?.[EnumNumberValue.zero]?.key]
    }
  },
  { immediate: true, deep: true }
)
const handleSearch = (searchText: string) => {
  if (props.searchData) {
    props.searchData(searchText)
  } else {
    changeTreeDataShow(searchText)
  }
}
const handleSearchDebounce = debounce(handleSearch, 100)
const handleChange = (e: Event) => {
  handleSearchDebounce((e.target as HTMLInputElement).value)
}
//选择
watch(
  (): [boolean, IValueType] => [open.value, props.selectedKeys],
  ([openVal, selectedKeys]: [boolean, IValueType]) => {
    if (!openVal) return
    if (isArray(selectedKeys)) {
      selectedKeysInModal.value = selectedKeys.slice()
    } else {
      selectedKeysInModal.value = selectedKeys ? ([selectedKeys] as IValueTypeMultiple) : []
    }
  },
  { immediate: true }
)
//勾选
watch(
  (): [boolean, IValueType] => [open.value, props.checkedKeys],
  ([openVal, checkedKeys]: [boolean, IValueType]) => {
    if (!openVal || !checkable.value) return
    if (isArray(checkedKeys)) {
      checkedKeysInModal.value = checkedKeys.slice()
    } else {
      checkedKeysInModal.value = checkedKeys ? ([checkedKeys] as IValueTypeMultiple) : []
    }
  },
  { immediate: true }
)
const slot = useSlots()

type ITreeSlotKeyType = 'icon' | 'title' | 'switcherIcon' | 'titleRender' | 'default' | 'leafIcon'
const treeSlot = omit(slot, ['selectedList']) as Record<ITreeSlotKeyType, Slot>
const deleteOneSelect = (key: IValueTypeSingle) => {
  if (checkable.value) {
    checkedKeysInModal.value = checkedKeysInModal.value.filter((item) => item !== key) as IValueTypeMultiple
  } else {
    selectedKeysInModal.value = selectedKeysInModal.value.filter((item) => item !== key) as IValueTypeMultiple
  }
}
const treeDataRef = toRef(() => props.treeData)
const { treeDataHistory } = useTreeDataMemory(treeDataRef)
const selectedItems = computed(() =>
  selectedKeysInModal.value.map((key) => {
    const item = treeGetItemFromSourceList(key, [props.treeData, treeDataHistory, props.treeDataDictionary ?? []])
    return item || { key, title: key }
  })
)
const checkedItems = computed(() =>
  checkedKeysInModal.value.map((key) => {
    const item = treeGetItemFromSourceList(key, [props.treeData, treeDataHistory, props.treeDataDictionary ?? []])
    return item || { key, title: key }
  })
)

defineExpose({ deleteOneSelect, selectedItems, checkedItems })
const emit = defineEmits<{
  confirm: [selectedKeys: IValueType, selectedItems?: IAcceptableType, checkedItems?: IAcceptableType, list?: IAcceptableType[]]
}>()
const handleOk = () => {
  const list = checkable.value ? checkedItems.value : selectedItems.value

  if (props.doNotCloseOnKeysNull && !list?.length) {
    emit('confirm', checkable.value ? checkedKeysInModal.value : selectedKeysInModal.value, [])
  } else {
    if (props.multiple || checkable.value) {
      emit('confirm', checkable.value ? checkedKeysInModal.value : selectedKeysInModal.value, list)
    } else {
      emit('confirm', checkable.value ? checkedKeysInModal.value[0] : selectedKeysInModal.value[0], list[0])
    }
    open.value = false
  }
}
const getSelectedList = (list: DataNode[]) => {
  const result = list?.filter((item) => {
    if (item.parentId && (checkedKeysInModal.value as unknown as DataNode[])?.includes(item.parentId)) {
      return false
    } else {
      return true
    }
  })
  return result
}
</script>
<style lang="scss" scoped>
.border-bottom-px {
  border-bottom: 1px solid #ccc;
  &:last-child {
    border-bottom: none;
  }
}
</style>
